import React, { useEffect, useState, useRef } from "react";
import { supabase } from "../../supabaseClient";
import { FaSpinner } from "react-icons/fa";

const IMAGE_FIELDS = [
  "poster",
  "portrait",
  "longPortrait",
  "background",
  "square",
  "banner",
  "featureBanner",
  "heroBanner",
  "titleLogo",
];

const MovieTile = ({ data }) => {
  const Movie = data[0];
  const [allSource, setAllSource] = useState({});

  const [allGenre, setAllGenre] = useState({});
  const [allDirectors, setAllDirectors] = useState({});
  const [allCastMembers, setAllCastMembers] = useState({});
  const imageRefs = useRef([]);
  const labelRefs = useRef([]);
  const [loading, setLoading] = useState(true);
  const minioBaseUrl = "https://images.do.cloudwalker.tv/images/";
  const titleLogoBaseUrl = "https://images.do.cloudwalker.tv/images/";
  
  const safeJsonParseArray = (value) => {
    if (!value) return [];
    if (typeof value === "string" && value.trim().startsWith("[") && value.trim().endsWith("]")) {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [value];
      } catch (err) {
        return [value];
      }
    }
    if (Array.isArray(value)) return value.filter(Boolean);
    return [value];
  };
  // Which image sits at index 0 of each field's array is the tile's "pinned"
  // image (same convention as the tile edit form) — kept as local state so
  // clicking an alternative below can reorder it and re-render immediately.
  const [imageState, setImageState] = useState(() => {
    const initial = {};
    IMAGE_FIELDS.forEach((field) => {
      initial[field] = safeJsonParseArray(Movie[field]);
    });
    return initial;
  });
  const [pinningField, setPinningField] = useState(null);

  useEffect(() => {
    const initial = {};
    IMAGE_FIELDS.forEach((field) => {
      initial[field] = safeJsonParseArray(Movie[field]);
    });
    setImageState(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Movie.tid]);

  const posterArray = imageState.poster;
  const portraitArray = imageState.portrait;
  const longPortraitArray = imageState.longPortrait;
  const backgroundArray = imageState.background;
  const squareArray = imageState.square;
  const bannerArray = imageState.banner;
  const featureBannerArray = imageState.featureBanner;
  const heroBannerArray = imageState.heroBanner;
  const titleLogoArray = imageState.titleLogo;

  const resolveImageUrl = (img, base) => {
    if (!img) return null;
    if (typeof img === "string" && img.startsWith("http")) return img;
    // Old migrated paths (e.g. "ref_id/filename.webp") are missing the "tiles/" prefix
    const path = img.startsWith("tiles/") ? img : `tiles/${img}`;
    return `${base}${path}`;
  };

  // Sets which image is pinned for a field — global to this tile (persisted
  // to the `tiles` table), so it applies everywhere the tile is used, not
  // just in this preview.
  const handlePinImage = async (field, path) => {
    const current = imageState[field] || [];
    if (current[0] === path) return;
    const reordered = [path, ...current.filter((p) => p !== path)];
    setImageState((prev) => ({ ...prev, [field]: reordered }));
    setPinningField(field);
    const { error } = await supabase
      .from("tiles")
      .update({ [field]: JSON.stringify(reordered) })
      .eq("tid", Movie.tid);
    setPinningField(null);
    if (error) {
      console.error(`Error pinning ${field} image:`, error.message);
      setImageState((prev) => ({ ...prev, [field]: current }));
    }
  };

  const ImageOptionsStrip = ({ field, images, baseUrl }) => {
    if (!images || images.length <= 1) return null;
    return (
      <div className="flex gap-1 mt-1 justify-center flex-wrap">
        {images.map((path, i) => (
          <button
            key={`${field}-${i}`}
            type="button"
            disabled={pinningField === field}
            onClick={() => handlePinImage(field, path)}
            title={i === 0 ? "Currently pinned everywhere this tile is used" : "Set as the pinned image"}
            className={`w-8 h-8 rounded overflow-hidden border-2 shrink-0 ${i === 0 ? "border-blue-500" : "border-gray-300 hover:border-blue-400"
              }`}
          >
            <img src={resolveImageUrl(path, baseUrl)} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    );
  };

  const isUUID = (val) =>
    typeof val === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);

  const fetchAll = async (table, selectFields) => {
    let all = [];
    let from = 0;
    const batch = 1000;

    while (true) {
      const { data, error } = await supabase
        .from(table)
        .select(selectFields)
        .range(from, from + batch - 1);

      if (error) {
        console.error(`Error fetching ${table}:`, error.message);
        break;
      }

      if (!data || data.length === 0) break;

      all = [...all, ...data];

      if (data.length < batch) break;

      from += batch;
    }

    return all;
  };

useEffect(() => {
  const isUUID = (v) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(v));

  const fetchOnlyNeeded = async () => {
    setLoading(true);

    try {
      // -------- SOURCE --------
      if (Array.isArray(Movie.source) && Movie.source.length > 0) {
        const uuids = Movie.source.filter(isUUID);
        const map = {};
        Movie.source.filter(v => !isUUID(v)).forEach(n => (map[n] = n));
        if (uuids.length > 0) {
          const { data } = await supabase.from("source").select("id, source").in("id", uuids);
          data?.forEach((s) => (map[s.id] = s.source));
        }
        setAllSource(map);
      }

      // -------- GENRE --------
      if (Array.isArray(Movie.genre) && Movie.genre.length > 0) {
        const rawIds = Movie.genre.map((g) => {
          if (typeof g === "string" && g.startsWith("{")) {
            try { return JSON.parse(g).value; } catch { return null; }
          }
          return g;
        }).filter(Boolean);

        const uuids = rawIds.filter(isUUID);
        const map = {};
        rawIds.filter(v => !isUUID(v)).forEach(n => (map[n] = n));
        if (uuids.length > 0) {
          const { data } = await supabase.from("genres").select("id, genres").in("id", uuids);
          data?.forEach((g) => (map[g.id] = g.genres));
        }
        setAllGenre(map);
      }

      // -------- DIRECTORS --------
      if (Array.isArray(Movie.director) && Movie.director.length > 0) {
        const uuids = Movie.director.filter(isUUID);
        const map = {};
        Movie.director.filter(v => !isUUID(v)).forEach(n => (map[n] = n));
        if (uuids.length > 0) {
          const { data } = await supabase.from("directors").select("id, directors").in("id", uuids);
          data?.forEach((d) => (map[d.id] = d.directors));
        }
        setAllDirectors(map);
      }

      // -------- CAST --------
      if (Array.isArray(Movie.cast) && Movie.cast.length > 0) {
        const rawIds = Movie.cast.map((c) => typeof c === "object" ? c.value : c);
        const uuids = rawIds.filter(isUUID);
        const map = {};
        rawIds.filter(v => !isUUID(v)).forEach(n => (map[n] = n));
        if (uuids.length > 0) {
          const { data } = await supabase.from("castMembers").select("id, castMembers").in("id", uuids);
          data?.forEach((c) => (map[c.id] = c.castMembers));
        }
        setAllCastMembers(map);
      }
    } catch (err) {
      console.error("Error fetching selective data:", err);
    }

    setLoading(false);
  };

  fetchOnlyNeeded();
}, [Movie]);

  // Align labels by adjusting margin-top based on max image height
  useEffect(() => {
    let maxHeight = 0;
    imageRefs.current.forEach((ref) => {
      if (ref) {
        const height = ref.getBoundingClientRect().height;
        if (height > maxHeight) maxHeight = height;
      }
    });

    imageRefs.current.forEach((ref, index) => {
      if (ref && labelRefs.current[index]) {
        const height = ref.getBoundingClientRect().height;
        const margin = maxHeight - height;
        labelRefs.current[index].style.marginTop = `${margin + 5}px`; // Add 8px for consistent spacing
      }
    });
  }, []); // Runs once after mount; heights are static


  
  const scrollRef = useRef(null);
  const scrollAmount = 200; // Pixels per button click

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -scrollAmount, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

if (loading) {
  return (
    <div className="flex flex-col items-center justify-center py-10 gap-3">
      <FaSpinner className="animate-spin text-3xl text-gray-600" />
      <p className="text-gray-600 font-medium">Loading tile...</p>
    </div>
  );
}

  return (
    <>
      <div className="max-w-5xl mx-auto p-8 bg-white shadow-lg rounded-lg">
        {/* Images Row with Scroll Buttons */}
        <div className="relative mb-8">
          <button
            onClick={scrollLeft}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-gray-200 hover:bg-gray-300 rounded-full p-2 shadow"
            aria-label="Scroll Left"
          >
            &lt;
          </button>
          <div
            ref={scrollRef}
            className="flex space-x-6 overflow-x-auto"
            style={{
              scrollbarWidth: "none", // Firefox
              msOverflowStyle: "none", // IE/Edge
            }}
          >
            {/* Inline style to hide scrollbar for Chrome/Safari */}
            <style>
              {`
                .overflow-x-auto::-webkit-scrollbar {
                  display: none;
                }
              `}
            </style>
            {/* Poster */}
            <div className="text-center flex-shrink-0 w-72">
              <div
                className="relative aspect-[16/9] overflow-hidden rounded-lg"
                ref={(el) => (imageRefs.current[0] = el)}
              >
                <img
                  className="absolute inset-0 w-full h-full object-contain"

                  src={
                    posterArray?.[0]
                      ? resolveImageUrl(posterArray[0], minioBaseUrl)
                      : "https://t3.ftcdn.net/jpg/05/04/28/96/360_F_504289605_zehJiK0tCuZLP2MdfFBpcJdOVxKLnXg1.jpg"
                  }

                  alt="Poster"
                />
              </div>
              <p
                className="mt-2 text-sm text-gray-600 font-medium"
                ref={(el) => (labelRefs.current[0] = el)}
              >
                Poster
              </p>
              <ImageOptionsStrip field="poster" images={posterArray} baseUrl={minioBaseUrl} />
            </div>

            {/* Portrait */}
            <div className="text-center flex-shrink-0 w-48">
              <div
                className="relative overflow-hidden rounded-lg"
                ref={(el) => (imageRefs.current[1] = el)}
              >
                <div className="relative w-[180px] h-[200px] overflow-hidden rounded-lg">
                  <img
                    className="w-full h-full object-contain rounded-lg"
                    src={
                      portraitArray?.[0]
                        ? resolveImageUrl(portraitArray[0], minioBaseUrl)
                        : "https://t3.ftcdn.net/jpg/05/04/28/96/360_F_504289605_zehJiK0tCuZLP2MdfFBpcJdOVxKLnXg1.jpg"
                    }
                    alt="Portrait"
                  />
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-600 font-medium" ref={(el) => (labelRefs.current[1] = el)}>
                Portrait
              </p>
              <ImageOptionsStrip field="portrait" images={portraitArray} baseUrl={minioBaseUrl} />
            </div>

            {/* Long Portrait */}
            <div className="text-center flex-shrink-0 w-36">
              <div className="relative w-[120px] h-[240px] overflow-hidden rounded-lg mx-auto">
                <img
                  className="w-full h-full object-contain rounded-lg"
                  src={
                    longPortraitArray?.[0]
                      ? resolveImageUrl(longPortraitArray[0], minioBaseUrl)
                      : "https://t3.ftcdn.net/jpg/05/04/28/96/360_F_504289605_zehJiK0tCuZLP2MdfFBpcJdOVxKLnXg1.jpg"
                  }
                  alt="Long Portrait"
                />
              </div>
              <p className="mt-2 text-sm text-gray-600 font-medium">Long Portrait</p>
              <ImageOptionsStrip field="longPortrait" images={longPortraitArray} baseUrl={minioBaseUrl} />
            </div>

            {/* Background */}
            <div className="text-center flex-shrink-0 w-72">
              <div
                className="relative aspect-[16/9] overflow-hidden rounded-lg"
                ref={(el) => (imageRefs.current[2] = el)}
              >
                <img
                  className="absolute inset-0 w-full h-full object-contain"

                  src={
                    backgroundArray?.[0]
                      ? resolveImageUrl(backgroundArray[0], minioBaseUrl)
                      : "https://t3.ftcdn.net/jpg/05/04/28/96/360_F_504289605_zehJiK0tCuZLP2MdfFBpcJdOVxKLnXg1.jpg"
                  }

                  alt="Background"
                />
              </div>
              <p
                className="mt-2 text-sm text-gray-600 font-medium"
                ref={(el) => (labelRefs.current[2] = el)}
              >
                Background
              </p>
              <ImageOptionsStrip field="background" images={backgroundArray} baseUrl={minioBaseUrl} />
            </div>
            {/* Square */}
            <div className="text-center flex-shrink-0 w-48">
              <img
                className="w-full h-48 object-cover rounded-lg"
                ref={(el) => (imageRefs.current[3] = el)}

                src={
                  squareArray?.[0]
                    ? resolveImageUrl(squareArray[0], minioBaseUrl)
                    : "https://t3.ftcdn.net/jpg/05/04/28/96/360_F_504289605_zehJiK0tCuZLP2MdfFBpcJdOVxKLnXg1.jpg"
                }

                alt="Square"
              />
              <p
                className="mt-2 text-sm text-gray-600 font-medium"
                ref={(el) => (labelRefs.current[3] = el)}
              >
                Square
              </p>
              <ImageOptionsStrip field="square" images={squareArray} baseUrl={minioBaseUrl} />
            </div>
            {/* Feature Banner */}
            <div className="text-center flex-shrink-0" style={{ minWidth: "300px" }}>
              <div
                className="relative overflow-hidden rounded-lg bg-gray-100"
                style={{ aspectRatio: "1920 / 300", width: "100%" }}
                ref={(el) => (imageRefs.current[5] = el)}
              >
                <img
                  className="absolute inset-0 w-full h-full object-fill"

                  src={
                    featureBannerArray?.[0]
                      ? resolveImageUrl(featureBannerArray[0], minioBaseUrl)
                      : "https://t3.ftcdn.net/jpg/05/04/28/96/360_F_504289605_zehJiK0tCuZLP2MdfFBpcJdOVxKLnXg1.jpg"
                  }

                  alt="Feature Banner"
                />
              </div>
              <p
                className="mt-2 text-sm text-gray-600 font-medium"
                ref={(el) => (labelRefs.current[5] = el)}
              >
                Feature Banner
              </p>
              <ImageOptionsStrip field="featureBanner" images={featureBannerArray} baseUrl={minioBaseUrl} />
            </div>
            {/* Hero Banner */}
            <div className="text-center flex-shrink-0" style={{ minWidth: "300px" }}>
              <div
                className="relative overflow-hidden rounded-lg bg-gray-100"
                style={{ aspectRatio: "1920 / 900", width: "100%" }}
                ref={(el) => (imageRefs.current[6] = el)}
              >
                <img
                  className="absolute inset-0 w-full h-full object-fill"

                  src={
                    heroBannerArray?.[0]
                      ? resolveImageUrl(heroBannerArray[0], minioBaseUrl)
                      : "https://t3.ftcdn.net/jpg/05/04/28/96/360_F_504289605_zehJiK0tCuZLP2MdfFBpcJdOVxKLnXg1.jpg"
                  }

                  alt="Hero Banner"
                />
              </div>
              <p
                className="mt-2 text-sm text-gray-600 font-medium"
                ref={(el) => (labelRefs.current[6] = el)}
              >
                Hero Banner
              </p>
              <ImageOptionsStrip field="heroBanner" images={heroBannerArray} baseUrl={minioBaseUrl} />
            </div>

            {/* Title Logo */}
            <div className="text-center flex-shrink-0" style={{ minWidth: "375px" }}>
              <div
                className="relative overflow-hidden rounded-lg bg-gray-100"
                style={{ width: "375px", height: "210px" }}   // FIXED SIZE 375x210px
                ref={(el) => (imageRefs.current[7] = el)}
              >
                <img
                  className="absolute inset-0 w-full h-full object-contain"
                  src={
                    titleLogoArray?.[0]
                      ? resolveImageUrl(titleLogoArray[0], titleLogoBaseUrl)
                      : "https://t3.ftcdn.net/jpg/05/04/28/96/360_F_504289605_zehJiK0tCuZLP2MdfFBpcJdOVxKLnXg1.jpg"
                  }
                  alt="Title Logo"
                />
              </div>
              <p
                className="mt-2 text-sm text-gray-600 font-medium"
                ref={(el) => (labelRefs.current[7] = el)}
              >
                Title Logo
              </p>
              <ImageOptionsStrip field="titleLogo" images={titleLogoArray} baseUrl={titleLogoBaseUrl} />
            </div>

            <div className="text-center flex-shrink-0" style={{ minWidth: "300px" }}>
              <div
                className="relative overflow-hidden rounded-lg bg-gray-100"
                style={{ aspectRatio: "1920 / 500", width: "100%" }} // height auto-calculated
                ref={(el) => (imageRefs.current[4] = el)}
              >
                <img
                  className="absolute inset-0 w-full h-full object-fill"
                  src={
                    bannerArray?.[0]
                      ? resolveImageUrl(bannerArray[0], minioBaseUrl)
                      : "https://t3.ftcdn.net/jpg/05/04/28/96/360_F_504289605_zehJiK0tCuZLP2MdfFBpcJdOVxKLnXg1.jpg"
                  }

                  alt="Banner"
                />
              </div>
              <p
                className="mt-2 text-sm text-gray-600 font-medium"
                ref={(el) => (labelRefs.current[4] = el)}
              >
                Masthead Banner
              </p>
              <ImageOptionsStrip field="banner" images={bannerArray} baseUrl={minioBaseUrl} />
            </div>
          </div>
          <button
            onClick={scrollRight}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-gray-200 hover:bg-gray-300 rounded-full p-2 shadow"
            aria-label="Scroll Right"
          >
            &gt;
          </button>
        </div>
        {/* Text Content */}
        <div className="space-y-4">
          {/* Title */}
          <h2 className="text-3xl font-bold text-gray-800">{Movie.title}</h2>

          <div className="text-gray-600">
            <span className="font-semibold">Genre:</span>{" "}
            {(() => {
              try {
                const genres = Movie.genre || [];
                if (!Array.isArray(genres) || genres.length === 0) return "N/A";

                const resolved = genres
                  .map((g) => {
                    // Case 1: g is a JSON string with label/value
                    if (typeof g === "string" && g.trim().startsWith("{")) {
                      try {
                        const parsed = JSON.parse(g);
                        return parsed.label || allGenre[parsed.value] || null;
                      } catch {
                        return null;
                      }
                    }
                    // Case 2: g is an ID stored in allGenre
                    if (allGenre[g]) return allGenre[g];
                    // Case 3: g is already a readable string
                    if (typeof g === "string") return g;
                    return null;
                  })
                  .filter(Boolean);

                return resolved.length > 0 ? resolved.join(", ") : "N/A";
              } catch {
                return "N/A";
              }
            })()}
          </div>


          {/* Run Time */}
          <div className="text-gray-600">
            <span className="font-semibold">Run Time:</span> {Movie.runtime}
          </div>
          {/* Year */}
          <div className="text-gray-600">
            <span className="font-semibold">Year:</span> {Movie.year}
          </div>
          {/* Source */}
          <div className="text-gray-600">
            <span className="font-semibold">Source:</span>{" "}
            {(() => {
              try {
                const sources = Movie.source;
                if (!Array.isArray(sources)) return "N/A";
                const resolved = sources
                  .map((id) => allSource[id] || id)
                  .filter(Boolean);
                return resolved.length > 0 ? resolved.join(", ") : "N/A";
              } catch (e) {
                return "N/A";
              }
            })()}
          </div>
          {/* Synopsis */}
          <div className="text-gray-600">
            <span className="font-semibold">Synopsis:</span> {Movie.synopsis}
          </div>
          {/* Director */}
          <div className="text-gray-600">
            <span className="font-semibold">Director:</span>{" "}
            {(() => {
              try {
                const dirs = Movie.director;
                if (!Array.isArray(dirs)) return "N/A";
                const resolved = dirs
                  .map((id) => allDirectors[id] || id)
                  .filter(Boolean);
                return resolved.length > 0 ? resolved.join(", ") : "N/A";
              } catch (e) {
                return "N/A";
              }
            })()}
          </div>
          {/* Cast Members */}
          <div className="text-gray-600">
            <span className="font-semibold">Cast Members:</span>{" "}
            {(() => {
              try {
                const castArr = Movie.cast;
                if (!Array.isArray(castArr)) return "N/A";
                const resolved = castArr
                  .map((val) => {
                    const id = typeof val === "object" ? val.value : val;
                    console.log(`Processing cast member: ${id}`);


                    // ✅ If already a name → return directly
                    if (!isUUID(id)) return id;

                    // ✅ If UUID → map to readable name
                    return allCastMembers[id] || id;
                  })
                  .filter(Boolean);
                console.log("After resolving cast members:", resolved);
                return resolved.length > 0 ? resolved.join(", ") : "N/A";
              } catch (e) {
                return "N/A";
              }
            })()}
          </div>
        </div>
      </div>
    </>
  );
};

export default MovieTile;